import { Plus, ShoppingCart, Trash, XMarkMini } from "@medusajs/icons"
import { clx, usePrompt } from "@medusajs/ui"
import { useState } from "react"
import { Link, useLocation, useNavigate } from "react-router-dom"
import { ago } from "../lib/format"
import { caps, t } from "../lib/i18n"
import { discardOpenOrder, newWorkbenchId, useOpenOrders, type OpenOrder } from "../lib/workbench"
import { BottomSheet } from "./add-to-order"

const sub = (order: OpenOrder) => `${t.n(order.items, "{n} item", "{n} items")} · ${ago(new Date(order.saved_at).toISOString())}`

/**
 * The custom orders being filled on this phone, as chips at the top of the order form: switch between
 * them (everything typed stays), start another, or drop one.
 */
export function OpenOrdersBar({ current, currentLabel }: { current: string; currentLabel: string }) {
  const navigate = useNavigate()
  const prompt = usePrompt()
  const orders = useOpenOrders(t("New order"))
  const list = orders.some((o) => o.id === current) ? orders : [{ id: current, saved_at: Date.now(), draft: {}, label: currentLabel, items: 0 }, ...orders]
  const drop = async (order: OpenOrder) => {
    const ok = await prompt({ title: t("Drop this order?"), description: t("«{order}» and everything typed in it will be removed from this phone. Nothing was written to the sheet.", { order: order.label }), confirmText: t("Drop it"), cancelText: t("Keep it") })
    if (ok) discardOpenOrder(order.id)
  }
  return (
    <div className="flex flex-col gap-y-1.5">
      <p className="hc-eyebrow">{caps(t("Orders you are filling"))}</p>
      <div className="hc-hscroll -mx-4 flex gap-x-1.5 px-4 py-0.5 lg:mx-0 lg:px-0" role="tablist" aria-label={t("Orders you are filling")}>
        {list.map((order) => {
          const active = order.id === current
          return (
            <span
              key={order.id}
              className={clx(
                "inline-flex h-10 shrink-0 items-center rounded-full border pl-3.5",
                active ? "bg-ui-fg-base text-ui-bg-base border-transparent pr-3.5" : "bg-ui-bg-base text-ui-fg-base border-ui-border-base pr-1"
              )}
            >
              <button type="button" role="tab" aria-selected={active} onClick={() => !active && navigate(`/custom/new?w=${encodeURIComponent(order.id)}`)} className="flex max-w-[180px] items-baseline gap-x-1.5">
                <span className="txt-compact-small-plus truncate">{active ? currentLabel : order.label}</span>
                {!active && order.items > 0 && <span className="hc-data text-ui-fg-muted text-[11px]">{order.items}</span>}
              </button>
              {!active && (
                <button type="button" onClick={() => void drop(order)} aria-label={t("Drop «{order}»", { order: order.label })} className="text-ui-fg-muted hover:text-ui-fg-base ml-0.5 rounded-full p-1.5">
                  <XMarkMini />
                </button>
              )}
            </span>
          )
        })}
        <button
          type="button"
          onClick={() => navigate(`/custom/new?w=${newWorkbenchId()}`)}
          className="txt-compact-small-plus text-ui-fg-interactive border-ui-border-base bg-ui-bg-base inline-flex h-10 shrink-0 items-center gap-x-1 rounded-full border border-dashed px-3.5"
        >
          <Plus /> {t("Another order")}
        </button>
      </div>
    </div>
  )
}

/**
 * «N open orders» wherever HQ is (but the order form itself): the orders being filled on this phone,
 * one tap away, so the finder, the stock or anything else can be checked mid-order.
 */
export function OpenOrdersDock() {
  const location = useLocation()
  const prompt = usePrompt()
  const orders = useOpenOrders(t("New order"))
  const [open, setOpen] = useState(false)
  // Owners, 21 Oct 2026: an order started and given up on can be removed from this list too.
  const drop = async (order: OpenOrder) => {
    const ok = await prompt({ title: t("Drop this order?"), description: t("«{order}» and everything typed in it will be removed from this phone. Nothing was written to the sheet.", { order: order.label }), confirmText: t("Drop it"), cancelText: t("Keep it") })
    if (!ok) return
    discardOpenOrder(order.id)
    if (orders.length <= 1) setOpen(false)
  }
  const hidden = location.pathname.startsWith("/custom/new") || location.pathname.startsWith("/products/") || location.pathname.startsWith("/print")
  if (!orders.length || hidden) return null
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hc-dock bg-ui-fg-base text-ui-bg-base txt-compact-small-plus fixed right-3 z-30 inline-flex h-11 items-center gap-x-2 rounded-full pl-3.5 pr-4 shadow-lg lg:right-8"
      >
        <ShoppingCart />
        {t.n(orders.length, "{n} open order", "{n} open orders")}
      </button>
      {open && (
        <BottomSheet title={t("Orders you are filling")} onClose={() => setOpen(false)}>
          <div className="flex flex-col">
            {orders.map((order) => (
              <div key={order.id} className="flex items-center gap-x-1">
                <Link
                  to={`/custom/new?w=${encodeURIComponent(order.id)}`}
                  onClick={() => setOpen(false)}
                  className="hover:bg-ui-bg-base-hover flex min-w-0 flex-1 items-center gap-x-3 rounded-lg px-2.5 py-2.5"
                >
                  <span className="bg-ui-bg-subtle text-ui-fg-subtle flex size-9 shrink-0 items-center justify-center rounded-lg">
                    <ShoppingCart />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="txt-compact-small-plus truncate">{order.label}</span>
                    <span className="txt-compact-xsmall text-ui-fg-muted">{sub(order)}</span>
                  </span>
                </Link>
                <button
                  type="button"
                  onClick={() => void drop(order)}
                  aria-label={t("Drop «{order}»", { order: order.label })}
                  className="text-ui-fg-muted hover:text-ui-fg-base hover:bg-ui-bg-base-hover flex size-11 shrink-0 items-center justify-center rounded-lg"
                >
                  <Trash />
                </button>
              </div>
            ))}
            <div className="border-ui-border-base my-2 border-t" />
            <Link to="/finder/saved" onClick={() => setOpen(false)} className="txt-compact-small-plus text-ui-fg-interactive px-2.5 py-2">
              {t("Your saved items")}
            </Link>
          </div>
        </BottomSheet>
      )}
    </>
  )
}
